import { Pencil, Trash2 } from 'lucide-react';
import { useState, type FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { LiksButton } from '../../../components/liks/LiksButton/LiksButton';
import { LiksConfirmDialog } from '../../../components/liks/LiksConfirmDialog/LiksConfirmDialog';
import { LiksDialog } from '../../../components/liks/LiksDialog/LiksDialog';
import {
  useDeleteChatbotMutation,
  useUpdateChatbotMutation,
} from '../../../features/chatbots/mutations/useCreateChatbotMutation';
import { useDeleteAllChatbotTrainingExamplesMutation } from '../../../features/chatbots/mutations/useChatbotTrainingExampleMutations';
import { formatTrainingCorrectionsProgressLabel } from '../../../features/chatbots/trainingCorrectionsProgress';
import { useI18n } from '../../../i18n/useI18n';
import { toastMutationError } from '../../../query/mutationToasts';
import { toast } from '../../../toast/toast';
import type { ChatbotWithIdentity } from '../../../types/chatbot';
import '../../knowledge/CreateKnowledgeDatabaseDialog.css';
import '../ChatbotPage.css';

type ChatbotSettingsTabProps = {
  chatbot: ChatbotWithIdentity;
};

export function ChatbotSettingsTab({ chatbot }: ChatbotSettingsTabProps) {
  const { translate } = useI18n();
  const navigate = useNavigate();
  const updateChatbotMutation = useUpdateChatbotMutation();
  const deleteChatbotMutation = useDeleteChatbotMutation();
  const deleteAllTrainingMutation = useDeleteAllChatbotTrainingExamplesMutation(
    chatbot.id,
  );
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [editName, setEditName] = useState('');
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [isDeleteTrainingDialogOpen, setIsDeleteTrainingDialogOpen] =
    useState(false);

  const chatbotName = chatbot.name;
  const isDangerActionPending =
    deleteChatbotMutation.isPending || deleteAllTrainingMutation.isPending;

  const beginEdit = () => {
    setEditName(chatbot.name);
    setIsEditModalOpen(true);
  };

  const closeEditModal = () => {
    setIsEditModalOpen(false);
  };

  const handleSaveChatbot = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    updateChatbotMutation.mutate(
      {
        chatbot,
        updates: {
          name: editName,
        },
      },
      {
        onSuccess: () => {
          toast.success({
            title: translate('chatbots.updateSuccessTitle'),
            description: '',
          });
          closeEditModal();
        },
        onError: (error) => {
          void toastMutationError(
            error,
            translate('chatbots.updateErrorTitle'),
          );
        },
      },
    );
  };

  const confirmDeleteChatbot = () => {
    if (deleteChatbotMutation.isPending) {
      return;
    }
    deleteChatbotMutation.mutate(chatbot.id, {
      onSuccess: () => {
        setIsDeleteDialogOpen(false);
        navigate('/chatbots');
      },
      onError: (error) => {
        void toastMutationError(
          error,
          translate('chatbots.deleteErrorTitle'),
          translate('chatbots.deleteErrorDescription'),
        );
      },
    });
  };

  const confirmDeleteAllTraining = () => {
    if (deleteAllTrainingMutation.isPending) {
      return;
    }
    deleteAllTrainingMutation.mutate(undefined, {
      onSuccess: (data) => {
        toast.success({
          title: translate('chatbots.settingsPage.deleteTrainingSuccessTitle'),
          description: translate(
            'chatbots.settingsPage.deleteTrainingSuccessDescription',
            {
              count: String(data ?? 0),
            },
          ),
        });
        setIsDeleteTrainingDialogOpen(false);
      },
      onError: (error) => {
        void toastMutationError(
          error,
          translate('chatbots.settingsPage.deleteTrainingErrorTitle'),
          translate('chatbots.deleteErrorDescription'),
        );
      },
    });
  };

  return (
    <>
      <section className="chatbot-page__panel chatbot-page__settings">
        <div className="chatbot-page__settings-section">
          <h2 className="chatbot-page__settings-title">
            {translate('chatbots.form.editTitle')}
          </h2>
          <p className="chatbot-page__settings-description">
            {translate('chatbots.settingsPage.editDescription')}
          </p>
          <LiksButton onClick={beginEdit}>
            <Pencil aria-hidden /> {translate('chatbots.form.editTitle')}
          </LiksButton>
        </div>
        <div className="chatbot-page__settings-section">
          <h2 className="chatbot-page__settings-title">
            {translate('chatbots.settingsPage.deleteTrainingTitle')}
          </h2>
          <p className="chatbot-page__settings-description">
            {translate('chatbots.settingsPage.deleteTrainingDescription', {
              name: chatbotName,
            })}
          </p>
          <LiksButton
            variant="danger"
            disabled={isDangerActionPending}
            onClick={() => setIsDeleteTrainingDialogOpen(true)}
          >
            <Trash2 aria-hidden />{' '}
            {translate('chatbots.settingsPage.deleteTrainingTitle')}
          </LiksButton>
        </div>
        <div className="chatbot-page__settings-section">
          <h2 className="chatbot-page__settings-title">
            {translate('chatbots.settingsPage.deleteTitle')}
          </h2>
          <p className="chatbot-page__settings-description">
            {translate('chatbots.settingsPage.deleteDescription', {
              name: chatbotName,
            })}
          </p>
          <LiksButton
            variant="danger"
            disabled={isDangerActionPending}
            onClick={() => setIsDeleteDialogOpen(true)}
          >
            <Trash2 aria-hidden />{' '}
            {translate('chatbots.settingsPage.deleteTitle')}
          </LiksButton>
        </div>
      </section>

      <LiksDialog
        isOpen={isEditModalOpen}
        as="form"
        size="md"
        title={translate('chatbots.form.editTitle')}
        onClose={closeEditModal}
        onSubmit={handleSaveChatbot}
        closeLabel={translate('chatbots.form.close')}
        footer={
          <>
            <LiksButton
              type="button"
              variant="secondary"
              onClick={closeEditModal}
            >
              {translate('chatbots.form.cancel')}
            </LiksButton>
            <LiksButton
              type="submit"
              variant="primary"
              disabled={
                editName.trim() === '' || updateChatbotMutation.isPending
              }
            >
              {translate('chatbots.form.save')}
            </LiksButton>
          </>
        }
      >
        <label className="liks-knowledge-create__field">
          <span className="liks-knowledge-create__label">
            {translate('chatbots.form.nameLabel')}
          </span>
          <input
            className="liks-knowledge-create__input"
            value={editName}
            onChange={(event) => setEditName(event.target.value)}
            placeholder={translate('chatbots.form.namePlaceholder')}
            required
            autoFocus
          />
        </label>
      </LiksDialog>

      <LiksConfirmDialog
        isOpen={isDeleteTrainingDialogOpen}
        title={translate('chatbots.settingsPage.deleteTrainingConfirmTitle')}
        message={translate(
          'chatbots.settingsPage.deleteTrainingConfirmMessage',
          {
            name: chatbotName,
          },
        )}
        confirmLabel={
          deleteAllTrainingMutation.isPending
            ? formatTrainingCorrectionsProgressLabel(
                translate('common.deleting'),
                deleteAllTrainingMutation.progress,
                true,
              )
            : translate('chatbots.deleteConfirm')
        }
        cancelLabel={translate('chatbots.deleteCancel')}
        isBusy={deleteAllTrainingMutation.isPending}
        onConfirm={() => void confirmDeleteAllTraining()}
        onCancel={() => {
          if (!deleteAllTrainingMutation.isPending) {
            setIsDeleteTrainingDialogOpen(false);
          }
        }}
      />

      <LiksConfirmDialog
        isOpen={isDeleteDialogOpen}
        title={translate('chatbots.deleteChatbotTitle')}
        message={translate('chatbots.deleteChatbotMessage', {
          name: chatbotName,
        })}
        confirmLabel={
          deleteChatbotMutation.isPending
            ? translate('common.deleting')
            : translate('chatbots.deleteConfirm')
        }
        cancelLabel={translate('chatbots.deleteCancel')}
        isBusy={deleteChatbotMutation.isPending}
        onConfirm={() => void confirmDeleteChatbot()}
        onCancel={() => {
          if (!deleteChatbotMutation.isPending) {
            setIsDeleteDialogOpen(false);
          }
        }}
      />
    </>
  );
}
